import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as TooltipUI } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';
import { LINE_CHART_COLORS } from '@/constants/colors';
import { WidgetData } from '@/types';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// Sourced from POST /api/widgets/key-metrics widget type 12 (TCX-6114). The backend already
// resolves bucket granularity (Hourly/Daily/Weekly/Monthly/Quarterly/Yearly) and returns a
// ready-to-display `label` per point (e.g. "Week29 2026", "Jul 2026", "Quarter1 2026",
// "2026") — the client just renders it as-is, no client-side spacing/label inference needed.
// `value` is the SLA percentage (0-100, rounded away from zero) already computed server-side;
// there is no client-side recomputation from raw call counts and no previous-period series.
interface SlaTrendDataPoint {
  label: string;
  value: number;
}

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
  dataKey: keyof SlaTrendDataPoint;
  payload: SlaTrendDataPoint;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  theme?: string;
}

interface SlaTrendWidgetProps {
  data: WidgetData;
  loading?: boolean;
}

const CustomTooltip = ({ active, payload, theme }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  return (
    <div className={`border rounded-lg shadow-md p-3 min-w-[220px] ${theme === "dark"
      ? "bg-gray-800 border-gray-600 text-white"
      : "bg-white border-gray-200 text-gray-900"
      }`}>
      <div style={{ color: theme === "dark" ? LINE_CHART_COLORS.TOOLTIP_TEXT.dark : LINE_CHART_COLORS.TOOLTIP_TEXT.light }} className="text-sm">
        SLA: {payload[0].value}%
      </div>
    </div>
  );
};

const SlaTrendWidget: React.FC<SlaTrendWidgetProps> = ({ data, loading }) => {
  const { theme } = useDashboard();

  const chartData = useMemo(() => {
    return (data?.value as SlaTrendDataPoint[]) ?? [];
  }, [data]);

  const renderContent = () => {
    if (loading) {
      return <Skeleton className="w-full h-[300px] rounded-none" />;
    }

    const isAllDataZero = chartData?.every(item =>
      item.value === 0 || item.value == null
    ) ?? false;

    return (
      <ResponsiveContainer width="99%" height={280}>
        <LineChart data={chartData} margin={{ left: 0, right: 20, top: 20, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
          />
          <YAxis
            tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
            domain={isAllDataZero ? [0, 100] : undefined}
            ticks={isAllDataZero ? [0, 20, 40, 60, 80, 100] : undefined}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip theme={theme} />} cursor={{ stroke: theme === "dark" ? '#4b5563' : '#d1d5db' }}/>
          {!isAllDataZero && (
            <Line
              type="monotone"
              dataKey="value"
              name="SLA (%)"
              stroke={theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light}
              strokeWidth={2}
              dot={{ r: 5, stroke: theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, strokeWidth: 2, fill: theme === "dark" ? LINE_CHART_COLORS.DOT_FILL.dark : LINE_CHART_COLORS.DOT_FILL.light }}
              activeDot={{ r: 7, fill: theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, stroke: theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, strokeWidth: 2 }}
              connectNulls={true}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card className="w-full overflow-hidden min-h-[300px] dark:border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <span>SLA (%) Trend</span>
          <TooltipProvider>
            <TooltipUI delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="cursor-pointer">
                  <Info className="h-4 w-4 text-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="max-w-xs text-xs font-normal">
                  The pattern according to the percentage of total incoming calls handled within 60 seconds within the given time.
                </p>
              </TooltipContent>
            </TooltipUI>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          {renderContent()}
        </div>
      </CardContent>
    </Card>
  );
};

export default SlaTrendWidget;
